import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

// Hold reset functions for all stores declared in the app
export const storeResetFns = new Set();

/**
 * Reset all stores to their initial state.
 * This is used in tests to reset the state of all stores between each test.
 * @warning This should only be used in tests!  */
export function __resetAllStores() {
  storeResetFns.forEach(fn => fn());
}

/**
 * Create a zustand store hook with redux devtools support.
 * @param creator function that creates the store
 * @param storeName name of the store, used to plug into redux devtools
 * @returns a zustand store hook
 */
export function createStore(creator) {
  // https://github.com/pmndrs/zustand#typescript-usage
  const store = create()(subscribeWithSelector(creator));

  // When creating a store, we get its initial state and create a 'reset' function and add it to the `storeResetFns` set
  // `storeResetFns` is used in tests to reset the state of all stores between each test
  const initialState = store.getState();
  storeResetFns.add(() => store.setState(initialState, true));
  return store;
}
/**
 * Create a zustand slice.
 * @param initialState default data for initialization and resets
 * @param actions function to retrieve actions to be added to the store.
 * @returns a zustand slice
 */
export function createSlice(initialState, actions) {
  return (set, get, api) => {
    const resetState = () => set(initialState);
    const getInitialState = () => initialState;
    storeResetFns.add(resetState);
    return Object.assign({}, initialState, {
      resetState,
      getInitialState
    }, actions(set, get, api));
  };
}